
/**
 * Utilities for detecting and communicating with the Clustr browser extension
 */

// Extension message types
export const EXTENSION_MESSAGES = {
  PING: 'CLUSTR_EXTENSION_PING',
  PING_RESPONSE: 'CLUSTR_EXTENSION_PING_RESPONSE',
  RE_WEB_CLIP: 'CLUSTR_RE_WEB_CLIP',
  RE_WEB_CLIP_RESPONSE: 'CLUSTR_RE_WEB_CLIP_RESPONSE',
  AUTH_STATUS: 'CLUSTR_AUTH_STATUS',
  COLLECTIONS_LIST: 'CLUSTR_COLLECTIONS_LIST',
  REQUEST_AUTH_STATUS: 'CLUSTR_REQUEST_AUTH_STATUS',
  REQUEST_COLLECTIONS: 'CLUSTR_REQUEST_COLLECTIONS',
};

// Check if extension is installed by sending a ping message
export const checkExtensionInstalled = async (): Promise<boolean> => {
  return new Promise((resolve) => {
    // Set a timeout to resolve false if no response in 500ms
    const timeoutId = setTimeout(() => {
      resolve(false);
    }, 500);

    // Listen for ping response
    const messageHandler = (event: MessageEvent) => {
      if (event.data && event.data.type === EXTENSION_MESSAGES.PING_RESPONSE) {
        clearTimeout(timeoutId);
        window.removeEventListener('message', messageHandler);
        resolve(true);
      }
    };

    window.addEventListener('message', messageHandler);

    // Send ping message
    window.postMessage({ 
      type: EXTENSION_MESSAGES.PING 
    }, '*');
  });
};

// Interface for re-web-clip response from extension
export interface ExtensionWebClipResponse {
  success: boolean;
  metadata?: {
    title?: string;
    description?: string;
    thumbnailUrl?: string;
    contentType?: string;
  };
  error?: string;
}

// Request re-web-clipping via extension
export const requestExtensionReWebClip = async (url: string): Promise<ExtensionWebClipResponse> => {
  return new Promise((resolve) => {
    // Set a timeout to resolve with an error if no response in 10 seconds
    const timeoutId = setTimeout(() => {
      resolve({ 
        success: false, 
        error: 'Extension timeout - no response received' 
      });
    }, 10000);

    // Listen for re-web-clip response
    const messageHandler = (event: MessageEvent) => {
      if (event.data && event.data.type === EXTENSION_MESSAGES.RE_WEB_CLIP_RESPONSE) {
        clearTimeout(timeoutId);
        window.removeEventListener('message', messageHandler);
        
        // Check if the extension payload indicates a site is blocking access
        if (event.data.payload.error && 
            (event.data.payload.error.includes('blocked') || 
             event.data.payload.error.includes('403') || 
             event.data.payload.error.includes('access denied'))) {
          resolve({
            success: false,
            error: 'The website is blocking our web clipper. Try setting a manual thumbnail instead.'
          });
        } else {
          resolve(event.data.payload);
        }
      }
    };

    window.addEventListener('message', messageHandler);

    // Send re-web-clip request
    window.postMessage({ 
      type: EXTENSION_MESSAGES.RE_WEB_CLIP,
      payload: { url }
    }, '*');
  });
};

// Helper function to validate collection ID for extension saves
export const validateCollectionForExtension = (collectionId: string): string => {
  // If no collection is provided or it's an invalid/empty string, default to INBOX_ID
  if (!collectionId || collectionId.trim() === '' || collectionId === 'none') {
    return 'inbox'; // INBOX_ID constant value
  }
  
  // If it's already the inbox ID, return as-is
  if (collectionId === 'inbox') {
    return collectionId;
  }
  
  // For real collection IDs, validate they look like UUIDs
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  if (uuidRegex.test(collectionId)) {
    return collectionId;
  }
  
  // If validation fails, default to inbox
  console.warn(`[Extension] Invalid collection ID "${collectionId}", defaulting to In Box`);
  return 'inbox';
};
